import React, { useEffect, useState } from "react";
import { FaEnvelope, FaCommentDots, FaLayerGroup } from "react-icons/fa";

/**
 * AutomationTypeStep
 * ------------------------------------------------------------------
 * Step 2 of the brand DM automation wizard. Lets the brand choose the
 * trigger type. Today only "DM" is supported (matching the influencer
 * flow); we keep "Comments" and "Both" visible but disabled so the
 * roadmap is clear to users.
 */
const TYPES = [
  {
    key: "DM",
    label: "Direct Message",
    description:
      "Send a private DM to the user as soon as their comment matches a keyword.",
    Icon: FaEnvelope,
    disabled: false,
  },
  {
    key: "Comments",
    label: "Comment Reply",
    description: "Reply publicly under the matched comment.",
    Icon: FaCommentDots,
    disabled: true,
  },
  {
    key: "Both",
    label: "DM + Comment",
    description: "Send a DM and post a public comment reply.",
    Icon: FaLayerGroup,
    disabled: true,
  },
];

const AutomationTypeStep = ({ data, onNext, onBack }) => {
  const [selected, setSelected] = useState(data?.type || "DM");

  useEffect(() => {
    if (data?.type) setSelected(data.type);
  }, [data?.type]);

  const handleSubmit = (event) => {
    event.preventDefault();
    onNext({ typeOfAutomation: { type: selected } });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <header>
        <h2 className="text-base md:text-xl font-semibold text-gray-900">
          Type of Automation
        </h2>
        <p className="mt-1 text-[11px] md:text-sm text-gray-600">
          Choose what should happen when a comment matches your keywords.
        </p>
      </header>

      <div className="space-y-2">
        {TYPES.map(({ key, label, description, Icon, disabled }) => {
          const isActive = selected === key;
          return (
            <label
              key={key}
              className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition ${
                isActive
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-200 bg-white hover:border-gray-300"
              } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
            >
              <input
                type="radio"
                name="automationType"
                value={key}
                checked={isActive}
                disabled={disabled}
                onChange={() => !disabled && setSelected(key)}
                className="mt-1 h-4 w-4 accent-orange-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-semibold text-gray-900">
                    {label}
                  </span>
                  {disabled && (
                    <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                      Coming soon
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[11px] md:text-sm text-gray-600">
                  {description}
                </p>
              </div>
            </label>
          );
        })}
      </div>

      <div className="flex justify-between pt-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
        >
          Back
        </button>
        <button
          type="submit"
          className="rounded-lg bg-orange-600 px-5 py-2 text-sm font-semibold text-white transition hover:bg-orange-700"
        >
          Next
        </button>
      </div>
    </form>
  );
};

export default AutomationTypeStep;
